import React, { useState } from 'react'
import { 
  Package, 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  ArrowUpDown,
  AlertTriangle, 
  Truck,
  Zap,
  Globe,
  ExternalLink,
  X,
  Info,
  ArrowRight
} from 'lucide-react'
import { Link } from 'wouter'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import { useInventoryManagement } from '../lib/data-service'
import { formatCurrency, formatNumber } from '../lib/utils'
import GlobalHeader from '../components/GlobalHeader'
import InfoTooltip from '../components/InfoTooltip'

interface FilterState {
  status: string[]
  chips: string[]
  dc_codes: string[]
  wos_store_band: string[]
  wos_dc_band: string[]
}

interface PerDCDrawerProps {
  isOpen: boolean
  onClose: () => void
  sku: string
  name?: string
  perDCData?: Array<{
    code: string
    eoh_units?: number | null
    wos?: number | null
    atp_units?: number | null
    on_order_units?: number | null
  }>
}

const PerDCDrawer: React.FC<PerDCDrawerProps> = ({ isOpen, onClose, sku, name, perDCData }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-foreground">Per-DC Breakdown</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {sku} {name && `- ${name}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-secondary rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60">
                <th className="text-left py-2 font-medium text-muted-foreground">DC Code</th>
                <th className="text-right py-2 font-medium text-muted-foreground">EOH Units</th>
                <th className="text-right py-2 font-medium text-muted-foreground">WOS</th>
                <th className="text-right py-2 font-medium text-muted-foreground">ATP Units</th>
                <th className="text-right py-2 font-medium text-muted-foreground">On Order</th>
                <th className="text-left py-2 font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {perDCData?.map((dc) => (
                <tr key={dc.code} className="border-b border-border/30">
                  <td className="py-2 font-mono text-foreground">{dc.code}</td>
                  <td className="py-2 text-right text-foreground">
                    {dc.eoh_units !== null ? formatNumber(dc.eoh_units) : '—'}
                  </td>
                  <td className="py-2 text-right">
                    {dc.wos !== null ? (
                      <span className={`font-medium ${
                        dc.wos < 1 ? 'text-red-600' : 
                        dc.wos < 2 ? 'text-yellow-600' : 
                        'text-foreground'
                      }`}>
                        {dc.wos.toFixed(1)}w
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-2 text-right text-foreground">
                    {dc.atp_units !== null ? formatNumber(dc.atp_units) : '—'}
                  </td>
                  <td className="py-2 text-right">
                    {dc.on_order_units !== null ? (
                      <span className={`${dc.on_order_units >= 100 ? 'font-medium text-blue-600' : 'text-foreground'}`}>
                        {formatNumber(dc.on_order_units)}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-2">
                    <div className="flex gap-1">
                      {dc.wos !== null && dc.wos < 1 && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                          Critical
                        </span>
                      )}
                      {dc.on_order_units !== null && dc.on_order_units >= 100 && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                          PO Active
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default function InventoryManagementEnhanced() {
  const { selectedFiscalWeek: fiscalWeek } = useFiscalWeekContext()
  const [activeTab, setActiveTab] = useState<'priority' | 'balance' | 'matrix'>('priority')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState('priority')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  
  // Auto-adjust sort when tab changes
  React.useEffect(() => {
    if (activeTab === 'priority') {
      setSortField('priority')
      setSortDir('desc')
    } else if (activeTab === 'balance') {
      setSortField('imbalance')
      setSortDir('desc')
    }
  }, [activeTab])
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedSku, setSelectedSku] = useState<string | null>(null)
  const [filters, setFilters] = useState<FilterState>({
    status: [],
    chips: [],
    dc_codes: [],
    wos_store_band: [],
    wos_dc_band: []
  })

  const { data, isLoading, error } = useInventoryManagement(
    fiscalWeek,
    searchQuery,
    sortField,
    sortDir,
    page,
    pageSize,
    filters,
    activeTab === 'matrix' || selectedSku !== null
  )

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
    setPage(1)
  }

  const toggleFilter = (category: keyof FilterState, value: string) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(v => v !== value)
        : [...prev[category], value]
    }))
    setPage(1)
  }

  const getChipColor = (chip: string) => {
    switch (chip) {
      case 'po_incoming':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'atp_tight':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'dotcom_risk':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getChipIcon = (chip: string) => {
    switch (chip) {
      case 'po_incoming':
        return <Truck className="w-3 h-3" />
      case 'atp_tight':
        return <Zap className="w-3 h-3" />
      case 'dotcom_risk':
        return <Globe className="w-3 h-3" />
      default:
        return null
    }
  }

  const getChipLabel = (chip: string) => {
    switch (chip) {
      case 'po_incoming':
        return 'PO Incoming'
      case 'atp_tight':
        return 'ATP Tight'
      case 'dotcom_risk':
        return 'Dotcom Risk'
      default:
        return chip
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'dc_starved':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'overstock_dc':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-green-100 text-green-800 border-green-200'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'dc_starved':
        return 'DC Starved'
      case 'overstock_dc':
        return 'Over-stocked at DC'
      default:
        return 'Healthy'
    }
  }

  const getImbalanceBar = (imbalance: number | null) => {
    if (imbalance === null) return <span className="text-muted-foreground text-xs">—</span>
    
    const maxWidth = 50 // pixels for bar portion
    const clampedImbalance = Math.max(-15, Math.min(15, imbalance))
    const percentage = Math.abs(clampedImbalance) / 15
    const width = percentage * maxWidth
    
    return (
      <div className="flex items-center justify-center gap-2">
        <div className="relative w-24 h-6 flex items-center">
          <div className="w-full h-2 bg-border rounded-full relative">
            {/* Center line */}
            <div className="absolute top-1/2 left-1/2 w-px h-4 bg-muted-foreground/40 transform -translate-x-1/2 -translate-y-1/2"></div>
            {/* Imbalance bar */}
            <div
              className={`absolute top-0 h-full rounded-full transition-all ${
                imbalance > 0 ? 'bg-blue-500 left-1/2' : 'bg-orange-500 right-1/2'
              }`}
              style={{ width: `${width}px` }}
            ></div>
          </div>
        </div>
        <span className={`text-xs font-medium min-w-[42px] text-right ${
          imbalance > 0 ? 'text-blue-600' : imbalance < 0 ? 'text-orange-600' : 'text-muted-foreground'
        }`}>
          {imbalance > 0 ? '+' : ''}{imbalance.toFixed(1)}w
        </span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-destructive">Error loading inventory data: {error.message}</p>
          </div>
        </div>
      </div>
    )
  }

  const selectedSkuData = selectedSku ? data?.rows.find(row => row.sku === selectedSku) : null

  return (
    <div className="min-h-screen bg-background">
      {/* Global Header with Date Picker */}
      <GlobalHeader
        title="Inventory Management"
        subtitle="DC balance, flow signals, and dotcom protection"
      />

      {/* Additional Info Bar */}
      <div className="bg-muted/30 border-b border-border/40">
        <div className="max-w-7xl mx-auto px-6 py-2">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-4">
              <span>Week {fiscalWeek || 'Not Selected'}</span>
              <span>•</span>
              <span>{data?.totalRowCount || 0} SKUs</span>
            </div>
            <Link href="/agents/supply-chain">
              <div className="flex items-center gap-2 cursor-pointer hover:text-primary transition-colors">
                <Truck className="w-4 h-4" />
                <span>Supply Chain Agent</span>
                <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header KPIs */}
        {data?.totals && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total SKUs</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatNumber(data.totals.sku_count)}
                  </p>
                </div>
                <Package className="w-5 h-5 text-primary" />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Store EOH</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatNumber(data.totals.store_eoh_units)}
                  </p>
                </div>
                <Package className="w-5 h-5 text-orange-500" />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">DC EOH</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatNumber(data.totals.dc_eoh_units)}
                  </p>
                </div>
                <Package className="w-5 h-5 text-blue-500" />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">DC ATP</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatNumber(data.totals.dc_atp_units)}
                  </p>
                </div>
                <Zap className="w-5 h-5 text-green-500" />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">On Order</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatNumber(data.totals.dc_on_order_units)}
                  </p>
                </div>
                <Truck className="w-5 h-5 text-purple-500" />
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8">
            {[
              { id: 'priority', label: 'Move Now (Priority)', desc: 'Sorted by priority score' },
              { id: 'balance', label: 'Balance', desc: 'DC vs Store imbalance focus' },
              { id: 'matrix', label: 'Per-DC Matrix', desc: 'DC code breakdown' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <div>
                  <div>{tab.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{tab.desc}</div>
                </div>
              </button>
            ))}
          </nav>
        </div>

        {/* Filters and Search */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-foreground">Filters & Search</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <Filter className="w-4 h-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search SKUs or product names..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="space-y-4 pt-4 border-t border-border">
              {/* Status Filters */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Status</h4>
                <div className="flex flex-wrap gap-2">
                  {['dc_starved', 'overstock_dc'].map((status) => (
                    <button
                      key={status}
                      onClick={() => toggleFilter('status', status)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        filters.status.includes(status)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:bg-secondary'
                      }`}
                    >
                      {getStatusLabel(status)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chip Filters */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Risk Signals</h4>
                <div className="flex flex-wrap gap-2">
                  {['po_incoming', 'atp_tight', 'dotcom_risk'].map((chip) => (
                    <button
                      key={chip}
                      onClick={() => toggleFilter('chips', chip)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1 ${
                        filters.chips.includes(chip)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:bg-secondary'
                      }`}
                    >
                      {getChipIcon(chip)}
                      {getChipLabel(chip)}
                    </button>
                  ))}
                </div>
              </div>

              {/* WOS Band Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Store WOS</h4>
                  <div className="flex flex-wrap gap-2">
                    {['<2', '2-10', '>10'].map((band) => (
                      <button
                        key={band}
                        onClick={() => toggleFilter('wos_store_band', band)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                          filters.wos_store_band.includes(band)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border hover:bg-secondary'
                        }`}
                      >
                        {band} weeks
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">DC WOS</h4>
                  <div className="flex flex-wrap gap-2">
                    {['<2', '2-10', '>10'].map((band) => (
                      <button
                        key={band}
                        onClick={() => toggleFilter('wos_dc_band', band)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                          filters.wos_dc_band.includes(band)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border hover:bg-secondary'
                        }`}
                      >
                        {band} weeks
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Table */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60">
                    <th className="text-left py-3 font-medium text-muted-foreground sticky left-0 bg-card">
                      <button
                        onClick={() => handleSort('sku')}
                        className="flex items-center gap-1 hover:text-foreground"
                      >
                        SKU
                        {sortField === 'sku' && (
                          sortDir === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    </th>
                    <th className="text-right py-3 font-medium text-muted-foreground">
                      <button
                        onClick={() => handleSort('dc_wos')}
                        className="flex items-center gap-1 hover:text-foreground ml-auto"
                      >
                        DC WOS
                        {sortField === 'dc_wos' && (
                          sortDir === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    </th>
                    <th className="text-right py-3 font-medium text-muted-foreground">
                      <button
                        onClick={() => handleSort('store_wos')}
                        className="flex items-center gap-1 hover:text-foreground ml-auto"
                      >
                        Store WOS
                        {sortField === 'store_wos' && (
                          sortDir === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    </th>
                    <th className="text-center py-3 font-medium text-muted-foreground">
                      <div className="flex items-center gap-1 justify-center">
                        <button
                          onClick={() => handleSort('imbalance')}
                          className="flex items-center gap-1 hover:text-foreground"
                        >
                          Imbalance
                          {sortField === 'imbalance' && (
                            sortDir === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        <InfoTooltip content="Store WOS minus DC WOS. Positive (blue) = more inventory in stores than needed. Negative (orange) = need to move inventory from DC to stores. Target: close to 0 for optimal balance." />
                      </div>
                    </th>
                    <th className="text-right py-3 font-medium text-muted-foreground">
                      <button
                        onClick={() => handleSort('dotcom_wos')}
                        className="flex items-center gap-1 hover:text-foreground ml-auto"
                      >
                        Dotcom WOS
                        {sortField === 'dotcom_wos' && (
                          sortDir === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    </th>
                    <th className="text-right py-3 font-medium text-muted-foreground">
                      <button
                        onClick={() => handleSort('atp')}
                        className="flex items-center gap-1 hover:text-foreground ml-auto"
                      >
                        DC ATP
                        {sortField === 'atp' && (
                          sortDir === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    </th>
                    <th className="text-right py-3 font-medium text-muted-foreground">On Order</th>
                    <th className="text-right py-3 font-medium text-muted-foreground">Velocity</th>
                    <th className="text-left py-3 font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 10 }).map((_, i) => (
                      <tr key={i} className="border-b border-border/30">
                        <td className="py-3 sticky left-0 bg-card">
                          <div className="h-4 bg-secondary/50 rounded animate-pulse"></div>
                        </td>
                        {Array.from({ length: 9 }).map((_, j) => (
                          <td key={j} className="py-3">
                            <div className="h-4 bg-secondary/50 rounded animate-pulse"></div>
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    data?.rows.map((row) => (
                      <tr key={row.sku} className="border-b border-border/30 hover:bg-secondary/20">
                        <td className="py-3 sticky left-0 bg-card">
                          <div className="max-w-xs">
                            <div className="font-medium text-foreground truncate" title={row.sku}>
                              {row.sku}
                            </div>
                            {row.name && (
                              <div className="text-xs text-muted-foreground truncate mt-1" title={row.name}>
                                {row.name}
                              </div>
                            )}
                            {row.chips.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {row.chips.map((chip) => (
                                  <span
                                    key={chip}
                                    className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getChipColor(chip)}`}
                                  >
                                    {getChipIcon(chip)}
                                    {getChipLabel(chip)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 text-right">
                          {row.dc_wos !== null ? (
                            <span className={`font-medium ${
                              row.dc_wos < 2 ? 'text-red-600' : 
                              row.dc_wos > 10 ? 'text-yellow-600' : 
                              'text-foreground'
                            }`}>
                              {row.dc_wos.toFixed(1)}w
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-3 text-right">
                          {row.store_wos !== null ? (
                            <span className={`font-medium ${
                              row.store_wos < 2 ? 'text-red-600' : 
                              row.store_wos > 10 ? 'text-yellow-600' : 
                              'text-foreground'
                            }`}>
                              {row.store_wos.toFixed(1)}w
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-3 text-center">
                          {getImbalanceBar(row.imbalance_wos)}
                        </td>
                        <td className="py-3 text-right">
                          {row.dotcom_wos !== null ? (
                            <span className={`font-medium ${
                              row.dotcom_wos < 2 ? 'text-red-600' : 'text-foreground'
                            }`}>
                              {row.dotcom_wos.toFixed(1)}w
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-3 text-right text-foreground">
                          {row.dc_atp_units !== null ? formatNumber(row.dc_atp_units) : '—'}
                        </td>
                        <td className="py-3 text-right">
                          <div className="space-y-1">
                            {row.dc_on_order_units !== null && (
                              <div className="text-foreground">
                                DC: {formatNumber(row.dc_on_order_units)}
                              </div>
                            )}
                            {row.store_on_order_units !== null && (
                              <div className="text-muted-foreground text-xs">
                                Store: {formatNumber(row.store_on_order_units)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 text-right text-foreground">
                          {row.velocity_5w !== null ? `${formatNumber(row.velocity_5w)}/wk` : '—'}
                        </td>
                        <td className="py-3">
                          {row.status ? (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(row.status)}`}>
                              {getStatusLabel(row.status)}
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium border bg-green-100 text-green-800 border-green-200">
                              Healthy
                            </span>
                          )}
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedSku(row.sku)}
                              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Per-DC
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data && data.totalRowCount > pageSize && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, data.totalRowCount)} of {data.totalRowCount} SKUs
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-2 border border-border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {Math.ceil(data.totalRowCount / pageSize)}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(Math.ceil(data.totalRowCount / pageSize), page + 1))}
                    disabled={page >= Math.ceil(data.totalRowCount / pageSize)}
                    className="px-3 py-2 border border-border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Per-DC Drawer */}
      <PerDCDrawer
        isOpen={selectedSku !== null}
        onClose={() => setSelectedSku(null)}
        sku={selectedSku || ''}
        name={selectedSkuData?.name || undefined}
        perDCData={selectedSkuData?.per_dc}
      />
    </div>
  )
}
